import { delay } from '~utils/functions';

/** Default number of retry attempts for failed imports. */
const DEFAULT_RETRY_COUNT = 3;

/** Default delay (in ms) between retry attempts. */
const DEFAULT_RETRY_DELAY_MS = 1000;

/**
 * Retries an asynchronous import (or any promise-returning function) a given number of times
 * with a delay between retries.
 *
 * Useful for handling transient network errors (e.g., chunk load failures in React).
 *
 * @param fn - A function returning a promise (e.g., `() => import('./MyComponent')`).
 * @param retries - The maximum number of retry attempts before failing (default: 3).
 * @param delay - The delay in milliseconds between retry attempts (default: 1000).
 *
 * @example
 * ```tsx
 * const LazyComponent = React.lazy(() =>
 *   retryImport(() => import('./MyComponent'))
 * );
 * ```
 */
export async function retryImport<T>(
	fn: () => Promise<T>,
	retries = DEFAULT_RETRY_COUNT,
	delayInMS = DEFAULT_RETRY_DELAY_MS,
): Promise<T> {
	try {
		return await fn();
	} catch (err) {
		if (retries <= 0) {
			const isChunkError = err?.message?.includes(
				'Failed to fetch dynamically imported module',
			);

			if (isChunkError && typeof window !== 'undefined') {
				window.location.reload();
			}

			throw err;
		}

		await delay(delayInMS);

		return retryImport(fn, retries - 1, delayInMS);
	}
}
